import type * as PI from 'interfaces/project';

/**
 * Copies only non-private properties from a model, preserving nested values by
 * reference so callers can decide when and how to deep-clone the public shape.
 *
 * @param model Source object whose top-level private properties should be omitted.
 * @returns Shallow copy containing only top-level keys that do not start with `_`.
 */
function copyPublicProperties<TModel extends object>(model: TModel): Partial<TModel> {
	const publicModel: Partial<TModel> = {};
	const publicModelRecord = publicModel as Record<string, unknown>;

	Object.entries(model).forEach(([
		key,
		value,
	]) => {
		if (key.charAt(0) !== '_') {
			publicModelRecord[key] = value;
		}
	});

	return publicModel;
}

/**
 * Copies a record of models while removing private properties from each value.
 *
 * @param models Models keyed by their persisted identifier.
 * @returns New record with the same keys and public-only shallow copies as values.
 */
function copyPublicRecordValues<TModel extends object>(
	models: Record<string, TModel>,
): Record<string, Partial<TModel>> {
	const publicModels: Record<string, Partial<TModel>> = {};

	Object.entries(models).forEach(([
		id,
		model,
	]) => {
		publicModels[id] = copyPublicProperties(model);
	});

	return publicModels;
}

/**
 * Builds the product data shape that is safe to serialize for persistence,
 * history, and snapshots without visiting private runtime state.
 *
 * @param state Active {@link PI.ProductStateModel} module state.
 * @returns Public product data with private top-level, page, object, and spine fields removed.
 */
export function copyProductStateForSerialization(state: PI.ProductStateModel): PI.ProductDataModel {
	const data = copyPublicProperties(state) as PI.ProductDataModel;

	data.pages = copyPublicRecordValues(state.pages) as PI.ProductDataModel['pages'];
	data.objects = copyPublicRecordValues(state.objects) as PI.ProductDataModel['objects'];

	if (state.spine) {
		data.spine = copyPublicProperties(state.spine) as PI.PageModel;
	}

	return data;
}

/**
 * Copies a model while omitting a caller-provided list of top-level keys before
 * later serialization work can visit those values.
 *
 * @param model Source object to copy.
 * @param excludedKeys Top-level keys that must not be copied into the output.
 * @returns Shallow copy of the source model without the requested keys.
 */
export function copyPropertiesExcluding<TModel extends object>(
	model: TModel,
	excludedKeys: readonly (keyof TModel)[],
): Partial<TModel> {
	const filteredModel: Partial<TModel> = {};
	const filteredModelRecord = filteredModel as Record<string, unknown>;

	Object.entries(model).forEach(([
		key,
		value,
	]) => {
		if (!excludedKeys.includes(key as keyof TModel)) {
			filteredModelRecord[key] = value;
		}
	});

	return filteredModel;
}
